import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { SectionInfoProps } from '../components/common-components/sectionInfo';
import { Benefit, NetworkName, NetworkType, Request } from '../types/insurance-report-api.types';
import { ADD_CRED_BANNER_LOCAL_STORAGE_KEY } from './constants';

export const isFilterNetworkType = (value: string) =>
  value === NetworkType.InNetwork || value === NetworkType.OutOfNetwork;

export const constructPatientsFullName = (firstName: string, lastName: string) => {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (name) {
    return `${titleCase(name)}`;
  }
  return '';
};

export const getResponseSource = (requests: Request[]): 'Website' | 'EDI' => {
  const websiteRequest = (requests || []).find((request) => request.source === 'website');
  if (websiteRequest?.status !== 'error') {
    return 'Website';
  } else {
    return 'EDI';
  }
};

export const titleCase = (str: string, split = ' ') => {
  // First, replace any underscores with spaces.
  const processedStr = str?.replace(/_/g, ' ');

  const sentence = processedStr?.toLowerCase().split(split);
  for (let i = 0; i < sentence?.length; i++) {
    if (!sentence[i] || sentence[i]?.length === 0) sentence[i] = '';
    else sentence[i] = (sentence[i]?.[0]?.toUpperCase() ?? '') + sentence[i]?.slice(1);
  }
  return sentence?.join(' ');
};

export const showDollarSign = (value: number | string): string => {
  try {
    const numericValue = typeof value === 'string' && value !== '' ? Number(value) : value;
    if (typeof numericValue === 'number' && !isNaN(numericValue)) {
      const formattedValue = (numericValue / 100).toFixed(2);
      return `$${formattedValue}`;
    }
    return value?.toString();
  } catch (error) {
    return value?.toString();
  }
};

export const hasSearchValueInBenefit = (benefit: Benefit, searchValue: string) => {
  if (searchValue === '') {
    //considering search is not enabled and  returning true
    return true;
  }
  const normalizedSearchValue = String(searchValue).toLowerCase();

  return Object.values(benefit).some((value) => {
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).toLowerCase().includes(normalizedSearchValue);
    }
    return false;
  });
};

export const convertArrayToObject = (arr: string[]) => {
  return arr.reduce((acc, item) => {
    acc[item] = {};
    return acc;
  }, {} as Record<string, Record<string, Benefit[]>>);
};

export const converArrayToArrayOfObjects = (arr: string[]) => {
  return arr.reduce((acc, item) => {
    acc[item] = [] as Benefit[];
    return acc;
  }, {} as Record<string, Benefit[]>);
};

export const getFilterName = (name: string) => {
  switch (name) {
    case NetworkType.InNetwork:
      return 'In-Network';
    case NetworkType.OutOfNetwork:
      return 'Out-of-Network';
    default:
      return name;
  }
};

export const removeFieldsWithNoData = (reportData: SectionInfoProps[], printData: SectionInfoProps[]) => {
  const filteredReportData = reportData.filter((item) => item.value && item.value !== '-' && item.value !== '');
  const filteredPrintData = printData.filter((item) => item.value && item.value !== '-' && item.value !== '');
  return {
    report: filteredReportData,
    printData: filteredPrintData,
  };
};

export const formatSourceResponse = (source: string) => {
  return source.toLowerCase() === 'edi' ? 'EDI' : titleCase(source);
};

export const removeUnderScore = (str: string) => {
  const text = str.replace(/_/g, ' ');
  return (text[0] || '').toUpperCase() + text.slice(1);
};

export const reportDateFormat = (date: string) => {
  if (!date) return '';
  return formatDate(new Date(date), 'MMM D, YYYY');
};

export const initializeAddCredBannerState = (isEligible: boolean) => {
  if (!isEligible) {
    return false;
  }
  return getLocalStorageForAddCredBanner() !== 'true';
};

export const setLocalStorageForAddCredBanner = () => {
  localStorage.setItem(ADD_CRED_BANNER_LOCAL_STORAGE_KEY, 'true');
};

export const getLocalStorageForAddCredBanner = () => {
  return localStorage.getItem(ADD_CRED_BANNER_LOCAL_STORAGE_KEY);
};

export const getInsuranceType = (insuranceType: InsuranceType) => {
  if (!insuranceType) return '';
  const lowerCaseInsuranceType = (insuranceType || '').toLowerCase();
  if (lowerCaseInsuranceType.includes('primary')) {
    return i18next.t('Primary Insurance');
  } else if (lowerCaseInsuranceType.includes('secondary')) {
    return i18next.t('Secondary Insurance');
  } else {
    return '';
  }
};

export const getDefaultNetworkFilter = (persistedNetworkState: string) => {
  return persistedNetworkState || NetworkName.inNetwork;
};
export const getDefaultSearchValue = (persistedSearchValue: string) => {
  return persistedSearchValue || '';
};

export const isNotUndefinedOrNull = (value: any): boolean => {
  return value !== undefined && value !== null && value !== 0;
};
